import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HeaderApp } from '../app/scaffold/HeaderApp';
import { getInitialData } from './getInitialData';
import { getPageExperiencesData } from './getPageExperiencesData';
import { isMin1024pxWide } from '../app/utils/device/identifyDevice';
import { isPreview } from '../lib/url/timestamp';
import type { ClientInitialState, ClientPreloadedState } from './types';

const loadInitialState = async (): Promise<void> => {
  try {
    const { host, search } = window.location;
    const params = new URLSearchParams(
      isPreview(host) && search.length ? search : ''
    );
    const windowPreloadedState =
      (window.__PRELOADED_STATE__ as ClientPreloadedState | undefined) || {};

    const exp = windowPreloadedState.exp;

    // get client state from combined /state and /config endpoints
    const initialState = await getInitialData(
      isMin1024pxWide(),
      params,
      exp || '',
      windowPreloadedState.headerClientConfigQueryParameters || ''
    );

    const headerAppEl = document.getElementById('root');

    if (headerAppEl) {
      const experienceData = getPageExperiencesData();
      const features = {
        ...initialState.features,
        ...(windowPreloadedState.features || {}),
        ...(experienceData?.length && {
          monetateExperiences: experienceData,
        }),
      };
      const hasFeatures = Object.keys(features).length > 0;

      const preloadedState: ClientInitialState = {
        ...initialState,
        ...windowPreloadedState,
        ...(hasFeatures && {
          features,
        }),
      };

      hydrateRoot(headerAppEl, <HeaderApp initialState={preloadedState} />);
      window.__PRELOADED_STATE__ = initialState;
      if (exp) {
        window.__PRELOADED_STATE__.exp = exp;
      }
    }
  } catch (err) {
    console.error(`Error hydrating application ${err}`);
  }
};

if (process.env.WITH_SCAFFOLD === 'true') {
  // When running locally within a consuming app we need to check if DOM is already ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadInitialState);
  } else {
    loadInitialState();
  }
} else {
  document.addEventListener('DOMContentLoaded', loadInitialState);
}
